<template>
  <div class="bg-white shadow rounded-lg p-5">
    <div class="flex items-start justify-between gap-4 mb-4">
      <div>
        <div class="text-sm text-slate-500 font-medium">
          Progression vers la récompense
        </div>
        <div v-if="fillLabel" class="flex items-baseline gap-2 mt-1">
          <span class="text-3xl font-semibold text-primary leading-none tabular-nums">{{ progressValue }}</span>
          <span class="text-lg text-slate-400 tabular-nums">/ {{ thresholdValue }}</span>
        </div>
      </div>
      <div class="text-right">
        <div v-if="ruleTeaser" class="text-xs text-slate-500">
          Règle du programme
        </div>
        <div v-if="ruleTeaser" class="text-[13px] font-semibold text-gray-dark max-w-[220px]">
          {{ ruleTeaser }}
        </div>
        <div class="text-xs font-semibold mt-1" :class="reached ? 'text-emerald-600' : 'text-primary'">
          {{ remainingLabel }}
        </div>
      </div>
    </div>

    <!-- Product / stamp model -->
    <div
      v-if="showStamps"
      class="bg-primary-lightest rounded-xl p-4 border border-dashed border-gray"
    >
      <div class="grid grid-cols-5 gap-2.5 max-w-[360px] mx-auto">
        <span
          v-for="stamp in stamps"
          :key="stamp.n"
          class="aspect-square rounded-full flex items-center justify-center"
          :class="stamp.filled ? 'bg-secondary shadow-sm' : 'border-2 border-dashed border-gray'"
        >
          <Icon v-if="stamp.filled" name="mdi-package-variant-closed" :size="16" color="#419e83" />
          <Icon v-else-if="stamp.n === thresholdValue" name="mdi-gift-outline" :size="16" color="#9ca3af" />
          <span v-else class="text-xs tabular-nums text-slate-400">{{ stamp.n }}</span>
        </span>
        <!-- The stamps past the threshold, in one extra cell right after the last stamp. -->
        <span
          v-if="productOverflow > 0"
          class="aspect-square rounded-full flex items-center justify-center border-2 border-solid border-secondary-darkest text-secondary-darkest text-sm font-semibold tabular-nums"
          :title="`${productOverflow} achat${productOverflow > 1 ? 's' : ''} au-delà du seuil`"
        >
          +{{ productOverflow }}
        </span>
      </div>
      <div v-if="owedPurchases > 0" class="text-center text-[11px] text-slate-500 mt-3.5">
        Produit offert en avance · {{ owedPurchases }} achat{{ owedPurchases > 1 ? 's' : '' }} à venir
      </div>
      <div v-else class="text-center text-[11px] text-slate-500 mt-3.5">
        La {{ thresholdValue }}<sup>e</sup> case déclenche la récompense
      </div>
    </div>

    <!-- Product model with a large threshold: fall back to a bar -->
    <div v-else-if="showProductBar" class="flex items-center gap-3">
      <div class="flex-1 h-3 bg-gray-light rounded-full overflow-hidden">
        <div class="h-full rounded-full bg-primary" :style="{ width: `${productPercent}%` }" />
      </div>
      <span class="text-xs font-semibold tabular-nums text-gray-dark">{{ progressValue }}/{{ thresholdValue }}</span>
    </div>

    <!-- Points model -->
    <div v-if="isPoint && pointThreshold > 0" class="mt-4">
      <div class="flex items-center justify-between text-xs text-slate-500 mb-1.5">
        <span>Points cumulés</span>
        <span class="tabular-nums">{{ pointProgress }} / {{ pointThreshold }}</span>
      </div>
      <div class="h-3 bg-gray-light rounded-full overflow-hidden">
        <div class="h-full rounded-full bg-primary" :style="{ width: `${pointPercent}%` }" />
      </div>
    </div>

    <div v-if="rewardsReady >= 1" class="mt-4 flex items-center gap-2 text-xs font-semibold text-emerald-700">
      <Icon name="mdi-gift" :size="15" color="#047857" />
      <span>{{ rewardsReady }} récompense{{ rewardsReady > 1 ? 's' : '' }} disponible{{ rewardsReady > 1 ? 's' : '' }} au comptoir</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, toRef} from "vue";
import {LoyaltyCardMembership} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import Icon from "@/core/components/icon/Icon.vue";
import useMembershipProgress from "@/loyalty-card-membership/composables/useMembershipProgress";

const props = defineProps<{
    membership: LoyaltyCardMembership;
}>();

const {
    isProduct,
    isPoint,
    productThreshold,
    productProgress,
    productRemaining,
    productOverflow,
    rewardsReady,
    pointThreshold,
    pointProgress,
    pointRemaining,
    ruleTeaser,
    fillLabel
} = useMembershipProgress(toRef(props, "membership"));

const showStamps = computed(() => isProduct.value && productThreshold.value > 0 && productThreshold.value <= 20);
const showProductBar = computed(() => isProduct.value && productThreshold.value > 20);

const progressValue = computed(() => (isProduct.value ? productProgress.value : pointProgress.value));
const thresholdValue = computed(() => (isProduct.value ? productThreshold.value : pointThreshold.value));

/**
 * Purchases still owed after a product offered in advance: the counter sits
 * below zero until the closing sales come in.
 */
const owedPurchases = computed(() => (isProduct.value ? Math.max(0, -productProgress.value) : 0));

/** One cell per stamp up to the threshold; the stamps past it share one extra cell. */
const stamps = computed(() => Array.from({length: productThreshold.value}, (_, index) => ({
    n: index + 1,
    filled: index < productProgress.value
})));

const productPercent = computed(() => (
    productThreshold.value ? Math.max(0, Math.min(100, (productProgress.value / productThreshold.value) * 100)) : 0
));
const pointPercent = computed(() => (
    pointThreshold.value ? Math.min(100, (pointProgress.value / pointThreshold.value) * 100) : 0
));

const remaining = computed(() => (isProduct.value ? productRemaining.value : pointRemaining.value));
const reached = computed(() => remaining.value === 0);
const remainingLabel = computed(() => {
    if (reached.value) {
        return "🎁 Récompense atteinte";
    }

    const unit = isProduct.value
        ? (remaining.value > 1 ? "produits" : "produit")
        : (remaining.value > 1 ? "points" : "point");

    return `Plus que ${remaining.value} ${unit} 🎁`;
});
</script>
